/**
 * Checkbox Primitive
 * Form checkbox component with validation states and design system tokens
 */

import React from "react";
import {
  Checkbox as ChakraCheckbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Text,
} from "@chakra-ui/react";
import { resolveSpacing } from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for checkbox tokens
declare const __checkbox_size_brand: unique symbol;
declare const __checkbox_state_brand: unique symbol;

export interface CheckboxSizeToken {
  readonly [__checkbox_size_brand]: true;
  readonly _value: string;
}

export interface CheckboxStateToken {
  readonly [__checkbox_state_brand]: true;
  readonly _value: string;
}

// Factory functions
const createCheckboxSizeToken = (value: string): CheckboxSizeToken => ({ _value: value } as CheckboxSizeToken);
const createCheckboxStateToken = (value: string): CheckboxStateToken => ({ _value: value } as CheckboxStateToken);

export interface CheckboxProps {
  children?: React.ReactNode;
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: CheckboxSizeToken;
  state?: CheckboxStateToken;
  isChecked?: boolean;
  defaultChecked?: boolean;
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  value?: string;
  id?: string;
  "data-testid"?: string;
}

function CheckboxComponent({
  children,
  label,
  helperText,
  errorText,
  size,
  state,
  isChecked,
  defaultChecked,
  isIndeterminate = false,
  isDisabled = false,
  isRequired = false,
  onChange,
  onBlur,
  onFocus,
  name,
  value,
  id,
  "data-testid": testId,
}: CheckboxProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedSize = size || checkboxTokens.size.medium;
  const resolvedState = state || checkboxTokens.state.default;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        colorScheme: "red",
        borderColor: useColorModeValue(Color.primitive.red(500)),
      };
    } else if (isSuccess) {
      return {
        colorScheme: "green",
        borderColor: useColorModeValue(Color.primitive.green(500)),
      };
    } else if (isWarning) {
      return {
        colorScheme: "orange",
        borderColor: useColorModeValue(Color.primitive.orange(500)),
      };
    }

    return {
      colorScheme: "blue",
      borderColor: useColorModeValue(Color.border.primary),
    };
  };

  const getSizeProps = (size: CheckboxSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  const labelContent = label || children;

  return (
    <FormControl isInvalid={isInvalid} isRequired={isRequired}>
      <ChakraCheckbox
        {...sizeProps}
        {...stateColors}
        isChecked={isChecked}
        defaultChecked={defaultChecked}
        isIndeterminate={isIndeterminate}
        isDisabled={isDisabled}
        isRequired={isRequired}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
        value={value}
        id={id}
        data-testid={testId}
        _focus={{
          boxShadow: `0 0 0 2px ${useColorModeValue(Color.interactive.primary)}`,
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
        }}
      >
        {labelContent && (
          <Text
            color={useColorModeValue(Color.text.primary)}
            fontSize={sizeProps.fontSize}
          >
            {labelContent}
          </Text>
        )}
      </ChakraCheckbox>

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Checkbox tokens
const checkboxTokens = {
  size: {
    small: createCheckboxSizeToken("small"),
    medium: createCheckboxSizeToken("medium"),
    large: createCheckboxSizeToken("large"),
  } as const,

  state: {
    default: createCheckboxStateToken("default"),
    success: createCheckboxStateToken("success"),
    warning: createCheckboxStateToken("warning"),
    error: createCheckboxStateToken("error"),
  } as const,
};

// Public API - combines tokens and component
export const Checkbox = Object.assign(CheckboxComponent, checkboxTokens);
